import {Card, openConfirmDialog, Separator, Switch, type FormControl} from 'platform/components';
import {Grid, Heading, HStack, Space, Text, VStack} from 'platform/foundation';

import {useController, type UseFormReturn} from 'react-hook-form';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_CHECKOUT_RULE_CONDITIONS} from '../../../constants/EMPTY_CHECKOUT_RULE_CONDITIONS';
import type {CheckoutRuleFormValues} from '../types/CheckoutRuleFormValues';

interface Props {
  control: FormControl<CheckoutRuleFormValues>;
  formApi: UseFormReturn<CheckoutRuleFormValues>;
}

export function CheckoutRuleSourceTypes(props: Props) {
  const {field} = useController({control: props.control, name: 'sourceType'});
  const title = i18n.t('entity.checkout.labels.sourceType');
  const sources = [
    {
      value: 'service-order',
      label: i18n.t('entity.checkout.labels.allowServiceOrders'),
    },
    {value: 'direct-sale', label: i18n.t('entity.warehouse.labels.allowDirectSales')},
  ] as const;

  const changeSource = (sourceType: CheckoutRuleFormValues['sourceType']) => {
    const applyChange = () => {
      props.formApi.setValue(
        'conditions',
        {...EMPTY_CHECKOUT_RULE_CONDITIONS},
        {
          shouldDirty: true,
        }
      );

      props.formApi.setValue('sourceType', sourceType, {
        shouldDirty: true,
        shouldValidate: true,
      });
    };

    if (
      sourceType === null &&
      Object.values(props.formApi.getValues('conditions')).some(isNotNil)
    ) {
      openConfirmDialog({
        text: i18n.t('entity.checkout.labels.confirmRuleSourceChange'),
        onConfirm: applyChange,
      });

      return;
    }

    applyChange();
  };

  return (
    <section aria-label={title}>
      <VStack>
        <Separator />
        <Heading size={4}>{title}</Heading>
        <Space vertical={1} />
        <Text size="small" color="secondary">
          {i18n.t('entity.checkout.labels.ruleSourceDescription')}
        </Text>
        <Space vertical={4} />
        <Card variant="inlineGrey">
          <Grid columns={[1, 2, 4]} spacing={4} align="center">
            {sources.map((source) => (
              <HStack key={source.value} align="center" spacing={3}>
                <Switch
                  aria-label={source.label}
                  value={field.value === source.value}
                  isDisabled={
                    props.formApi.formState.isSubmitting ||
                    (field.value !== null && field.value !== source.value)
                  }
                  onChange={(enabled) => changeSource(enabled ? source.value : null)}
                />
                <Heading size={4}>{source.label}</Heading>
              </HStack>
            ))}
          </Grid>
        </Card>
      </VStack>
    </section>
  );
}
